import { Vector } from './Vector';
import type { Plane } from './Plane';

export type GridCell = {
    column: number;
    row: number;
};

export type GridObject<TType extends string = string> = {
    Id: string;
    Type: TType;
    Position: Vector;
    halfExtents: Vector;
    collisionPlanes?: Plane[];
    r?: number;
    g?: number;
    b?: number;
    index? : number;
    cells?: GridCell[];
};

export class GridObjectListItem<T extends GridObject<string> = GridObject> {
    Item: T | null = null;
    NextItem: GridObjectListItem<T> | null = null;
}

class GridObjectListItemPool<T extends GridObject<string> = GridObject> {
    private readonly gameObjectListItems: Array<GridObjectListItem<T>>;
    private freePointer: number;

    constructor(size: number) {
        this.freePointer = 0;
        const poolSize = Math.max(1, size * 4);
        this.gameObjectListItems = Array.from(
            { length: poolSize },
            () => new GridObjectListItem<T>()
        );
    }

    get(): GridObjectListItem<T> {
        if (this.freePointer >= this.gameObjectListItems.length) {
            throw new Error(`pool free pointer too large ${this.freePointer}`);
        }

        const item = this.gameObjectListItems[this.freePointer];
        this.freePointer += 1;
        item.Item = null;
        item.NextItem = null;
        return item;
    }

    reset(): void {
        this.freePointer = 0;
    }
}

export class Grid<T extends GridObject<string> = GridObject> {
    private readonly grid: Array<GridObjectListItem<T> | null>;
    private readonly gameObjectListItemPool: GridObjectListItemPool<T>;
    readonly cellSize: number;
    readonly width: number;
    readonly height: number;
    readonly rows: number;
    readonly columns: number;
    readonly translation: Vector;
    readonly location: Vector;
    readonly halfExtents: Vector;

    constructor(width: number, height: number, cellSize: number, noOfGameObjects: number, location: Vector = new Vector(0, 0, 0)) {
        this.width = width + 2 * cellSize;
        this.height = height + 2 * cellSize;
        this.columns = Math.trunc(this.width / cellSize);
        this.rows = Math.trunc(this.height / cellSize);
        this.cellSize = cellSize;
        this.grid = new Array(this.rows * this.columns).fill(null);
        this.gameObjectListItemPool = new GridObjectListItemPool<T>(noOfGameObjects);
        // The translation is used to convert between grid coordinates and world coordinates.
        this.halfExtents = new Vector(this.width / 2, -this.height / 2, 0);

        this.location = location;
        this.translation = Vector.subtract(location, this.halfExtents);
    }

    clearGrid(): void {
        this.gameObjectListItemPool.reset();
        this.grid.fill(null);
    }

    getCellContents(cell: GridCell): GridObjectListItem<T> | null {
        return this.grid[this.getIndex(cell)];
    }

    positionToCell(position: Vector): GridCell {

        const translated = Vector.add(position, Vector.scale(this.translation, -1));

        return {
            column: Math.trunc(translated.x / this.cellSize) + 1,
            row: Math.trunc(-translated.y / this.cellSize) + 1
        };
    }

    //    0       1        2       3       4       5    
    //                 |<-0.5->|<-0.5->|<-0.5->|
    // |<-0.5->|<-0.5->|<-0.5->|<-0.5->|<-0.5->|<-0.5->|
    // -1.75   -1.25   -0.75   -0.25   0.25    0.75    1.25                     
    cellCenterToPosition(cell: GridCell): Vector {
        const extents = new Vector(
            //(column - 1) * this.CellSize - Math.trunc(this.width / 2) + Math.trunc(this.CellSize / 2),
            //-(row - 1) * this.CellSize + Math.trunc(this.height / 2) - Math.trunc(this.CellSize / 2),
            //(column - 1) * this.CellSize - Math.trunc((this.width / 2) + (this.CellSize / 2)),
            //  0*50 = 0
            //  1*50 = 50 
            //  2*50 = 100
            //
            //  0*50 = 0
            // -1*50 = -50    + 100 = 50
            // -2*50 = -100   + 100 = 0
            + (this.cellSize / 2),
            - (this.cellSize / 2),
            0
        );

        return Vector.add(this.cellToPosition(cell), extents);
    }

    cellToPosition(cell: GridCell): Vector {
        const cellBoundary = new Vector(
            (cell.column - 1) * this.cellSize,
            -(cell.row - 1) * this.cellSize,
            0
        );

        return Vector.add(this.translation, cellBoundary);
    }

    CellObjectCount(column: number, row: number): number {
        let item = this.getCellContents({ column, row });
        let count = 0;
        while (item !== null) {
            count += 1;
            item = item.NextItem;
        }
        return count;
    }

    gridCells(gameObject: T): GridCell[] {
        const position = this.positionToTopLeftBoundingBox(gameObject);
        const gridCell = this.positionToCell(position);

        if (!this.IsInsideGrid(gridCell)) {
            return []; 
        }

        const cells: GridCell[] = [];
        cells.push(gridCell);

        const rightCell = this.cellToPosition({ column: gridCell.column + 1, row: gridCell.row });
        if (position.x + 2 * gameObject.halfExtents.x >= rightCell.x) {
            cells.push({ column: gridCell.column + 1, row: gridCell.row });
        }

        const belowCell = this.cellToPosition({ column: gridCell.column, row: gridCell.row + 1 });
        if (position.y - 2 * gameObject.halfExtents.y <= belowCell.y) {
            cells.push({ column: gridCell.column, row: gridCell.row + 1 });

            if (position.x + 2 * gameObject.halfExtents.x >= rightCell.x) {
                cells.push({ column: gridCell.column + 1, row: gridCell.row + 1 });
            }
        }

        gameObject.cells = cells;
        return cells;
    }

    addStatic(gameObject: T): void {
        const cells = this.gridCells(gameObject);
        for (const cell of cells) {
            this.addToList(cell, gameObject);
        }
    }

    addDynamic(gameObject: T): T[] {
        const seenColliders = new Set<string>();
        const colliders: T[] = [];

        const cells = this.gridCells(gameObject);
        for (const cell of cells) {
            this.addToListAndCheck(cell, gameObject, seenColliders, colliders);
        }

        return colliders;
    }

    private positionToTopLeftBoundingBox(gameObject: T): Vector {
        return new Vector(
            gameObject.Position.x - gameObject.halfExtents.x,
            gameObject.Position.y + gameObject.halfExtents.y,
            gameObject.Position.z
        );
    }

    private getIndex(cell: GridCell): number {
        if (cell.column <= 0 || cell.row <= 0) {
            throw new Error('column and row should be > 0');
        }

        return (cell.column - 1) + (cell.row - 1) * this.columns;
    }

    IsInsideGrid(cell: GridCell): boolean {
        return cell.column <= this.columns && cell.row <= this.rows && cell.row >= 1 && cell.column >= 1;
    }

    private addToList(
        cell: GridCell,
        gameObject: T,
    ): void {
        if (!this.IsInsideGrid(cell)) {
            return;
        }

        const index = this.getIndex(cell);

        this.PrependToCell(index, gameObject);
        return;

    }

    private addToListAndCheck(
        cell: GridCell,
        gameObject: T,
        seenColliders: Set<string>,
        colliders: T[],
    ): void {
        if (!this.IsInsideGrid(cell)) {
            return;
        }

        const index = this.getIndex(cell);
        let item = this.grid[index];

        while (item !== null) {
            if (item.Item !== null && this.DoCollideBox(gameObject, item.Item)) {
                const colliderKey = this.CreateObjectKey(item.Item);
                if (!seenColliders.has(colliderKey)) {
                    seenColliders.add(colliderKey);
                    colliders.push(item.Item);
                }

                if (item.Item.r !== undefined) {
                    item.Item.r = 1;
                }
                if (item.Item.g !== undefined) {
                    item.Item.g = 1;
                }
                if (item.Item.b !== undefined) {
                    item.Item.b = 0;
                }
            }
            item = item.NextItem;
        }

        this.PrependToCell(index, gameObject);
    }

    private PrependToCell(index: number, gameObject: T): void {
        const newItem = this.gameObjectListItemPool.get();
        newItem.Item = gameObject;
        newItem.NextItem = this.grid[index];
        this.grid[index] = newItem;
    }

    private CreateObjectKey(object: T): string {
        return `${object.Type}:${object.Id}`;
    }

    private DoCollideBox(objectA: T, objectB: T): boolean {
        const leftA = objectA.Position.x - objectA.halfExtents.x;
        const rightA = objectA.Position.x + objectA.halfExtents.x;
        const topA = objectA.Position.y + objectA.halfExtents.y;
        const bottomA = objectA.Position.y - objectA.halfExtents.y;

        const leftB = objectB.Position.x - objectB.halfExtents.x;
        const rightB = objectB.Position.x + objectB.halfExtents.x;
        const topB = objectB.Position.y + objectB.halfExtents.y;
        const bottomB = objectB.Position.y - objectB.halfExtents.y;

        return leftA <= rightB && rightA >= leftB && topA >= bottomB && bottomA <= topB;
    }
}